import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, CircleDashed, ArrowLeft, RefreshCcw, Search, UserPlus, X, QrCode, Camera } from "lucide-react";
import { formatEventDate } from "@/lib/utils";
import AddAttendeeDialog from "@/components/AddAttendeeDialog";
import { Html5Qrcode } from "html5-qrcode";

/**
 * Full-screen door-mode check-in view. Optimized for a host at the venue holding their phone.
 * - Big running count card at the top (checked in / paid)
 * - Fast search by name/email/ticket code (autofocus)
 * - Scan QR button → opens camera, auto-checks in on match
 * - Walk-up button → add + comp an attendee without leaving this view
 * - Auto-refresh every 20s so multiple hosts stay in sync
 */
export default function DoorMode() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("not-in"); // 'not-in' | 'all' | 'in'
  const [checking, setChecking] = useState(new Set());
  const [scannerOpen, setScannerOpen] = useState(false);
  const [walkupOpen, setWalkupOpen] = useState(false);
  const searchRef = useRef(null);

  const load = async () => {
    try {
      const [ev, atts] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/admin/events/${eventId}/attendees`),
      ]);
      setEvent(ev.data);
      setAttendees(atts.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load door list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => { searchRef.current?.focus(); }, []);

  const paid = attendees.filter(a => a.status === "paid");
  const checkedIn = paid.filter(a => !!a.checked_in_at);
  const totalGuests = paid.reduce((sum, a) => sum + (a.quantity || 1), 0);
  const checkedInGuests = checkedIn.reduce((sum, a) => sum + (a.quantity || 1), 0);

  const filtered = useMemo(() => {
    let list = paid;
    if (filter === "not-in") list = list.filter(a => !a.checked_in_at);
    else if (filter === "in") list = list.filter(a => a.checked_in_at);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(a =>
        (a.user?.name || "").toLowerCase().includes(q) ||
        (a.user?.email || "").toLowerCase().includes(q) ||
        (a.user?.phone || "").toLowerCase().includes(q) ||
        (a.ticket_code || "").toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      const aIn = !!a.checked_in_at, bIn = !!b.checked_in_at;
      if (aIn !== bIn) return aIn ? 1 : -1;
      return (a.user?.name || "").localeCompare(b.user?.name || "");
    });
    return list;
  }, [paid, filter, query]);

  const toggleCheckIn = async (ticket) => {
    const id = ticket.ticket_id;
    setChecking(prev => new Set(prev).add(id));
    try {
      if (ticket.checked_in_at) {
        await api.post(`/admin/tickets/${id}/undo-check-in`);
        toast.success(`${ticket.user?.name || "Attendee"} check-in reversed`);
      } else {
        await api.post(`/admin/tickets/${id}/check-in`);
        toast.success(`✓ ${ticket.user?.name || "Attendee"} is in`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-in failed");
    } finally {
      setChecking(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleScannedCode = async (rawCode) => {
    const code = (rawCode || "").trim();
    if (!code) return;
    try {
      const { data } = await api.post(`/admin/events/${eventId}/check-in-by-code`, { code });
      const name = data.user?.name || "Attendee";
      if (data.already_checked_in) {
        toast.info(`${name} was already checked in`);
      } else {
        toast.success(`✓ ${name} is in`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `No ticket for "${code}"`);
    }
  };

  const progressPct = totalGuests ? Math.round((checkedInGuests / totalGuests) * 100) : 0;

  return (
    <div className="min-h-screen bg-neutral-950 text-cream pb-20" data-testid="door-mode-page">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 bg-neutral-950/95 backdrop-blur border-b border-neutral-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/admin" className="text-neutral-400 hover:text-white shrink-0" data-testid="door-back"><ArrowLeft size={20} /></Link>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(46_65%_70%)]">Door check-in</div>
            <div className="text-sm font-heading truncate" data-testid="door-event-title">{event?.title || "Loading…"}</div>
            {event && <div className="text-[11px] text-neutral-400 truncate">{formatEventDate(event.date, { weekday: "short", month: "short", day: "numeric" })} · {event.time} · {event.venue}</div>}
          </div>
          <button onClick={load} className="text-neutral-400 hover:text-white shrink-0" title="Refresh" data-testid="door-refresh"><RefreshCcw size={18} /></button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-6 space-y-5">
        {/* Running count card */}
        <div className="rounded-3xl bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 p-6" data-testid="door-count-card">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-eyebrow text-neutral-400">Checked in</div>
              <div className="font-heading text-6xl md:text-7xl text-white tabular-nums leading-none mt-2" data-testid="door-count-checkedin">
                {checkedInGuests}
                <span className="text-neutral-500 text-3xl md:text-4xl"> / {totalGuests}</span>
              </div>
              <div className="text-xs text-neutral-500 mt-2">{checkedIn.length} of {paid.length} tickets</div>
            </div>
            <div className="text-right">
              <div className="font-heading text-5xl text-[hsl(46_65%_60%)] tabular-nums" data-testid="door-count-pct">{progressPct}%</div>
              <div className="text-eyebrow text-neutral-500 mt-1">in the room</div>
            </div>
          </div>
          <div className="mt-5 h-2 rounded-full bg-neutral-800 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Primary action bar */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={()=>setScannerOpen(true)}
            className="h-14 rounded-2xl bg-[hsl(46_65%_52%)] hover:bg-[hsl(46_65%_45%)] text-black font-heading text-base"
            data-testid="door-scan-btn"
          >
            <QrCode size={20} className="mr-2" /> Scan QR
          </Button>
          <Button
            onClick={()=>setWalkupOpen(true)}
            variant="outline"
            className="h-14 rounded-2xl bg-neutral-900 hover:bg-neutral-800 border-neutral-700 text-cream font-heading text-base"
            data-testid="door-walkup-btn"
          >
            <UserPlus size={20} className="mr-2" /> Walk-up
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            ref={searchRef}
            value={query}
            onChange={e=>setQuery(e.target.value)}
            placeholder="Search name, email, phone, or ticket code…"
            className="pl-10 pr-10 h-12 rounded-full bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500 focus-visible:ring-[hsl(46_65%_60%)]"
            data-testid="door-search"
            autoComplete="off"
          />
          {query && (
            <button onClick={()=>setQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white" aria-label="Clear" data-testid="door-search-clear">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 text-xs font-mono uppercase tracking-widest">
          {[
            { id: "not-in", label: `Waiting (${paid.length - checkedIn.length})` },
            { id: "in", label: `In (${checkedIn.length})` },
            { id: "all", label: `All (${paid.length})` },
          ].map(f => (
            <button
              key={f.id}
              onClick={()=>setFilter(f.id)}
              className={`px-4 py-2 rounded-full border transition ${filter === f.id ? "bg-cream text-black border-cream" : "border-neutral-700 text-neutral-400 hover:text-white"}`}
              data-testid={`door-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Attendee list */}
        <div className="space-y-2" data-testid="door-attendee-list">
          {loading ? (
            <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-8 text-center text-neutral-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-8 text-center text-neutral-500">
              {paid.length === 0 ? "No paid attendees yet." : filter === "not-in" ? "🎉 Everyone's in. Enjoy the night." : "Nobody matches that search."}
            </div>
          ) : filtered.map(a => {
            const isIn = !!a.checked_in_at;
            const busy = checking.has(a.ticket_id);
            return (
              <div key={a.ticket_id} className={`rounded-2xl border p-4 flex items-center gap-4 transition ${isIn ? "bg-emerald-950/40 border-emerald-800" : "bg-neutral-900 border-neutral-800"}`} data-testid={`door-row-${a.ticket_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <div className="font-heading text-lg text-white truncate">{a.user?.name || "Guest"}</div>
                    {a.quantity > 1 && <span className="text-[10px] font-mono uppercase tracking-widest bg-[hsl(46_65%_52%/0.2)] text-[hsl(46_65%_70%)] px-1.5 py-0.5 rounded">+{a.quantity - 1}</span>}
                    {a.admin_note && <span className="text-[10px] font-mono uppercase tracking-widest bg-neutral-700 text-neutral-300 px-1.5 py-0.5 rounded" title={a.admin_note}>comp</span>}
                  </div>
                  <div className="text-xs text-neutral-500 font-mono truncate">{a.user?.email}{a.ticket_code ? ` · ${a.ticket_code}` : ""}</div>
                  {isIn && <div className="text-[11px] text-emerald-400 mt-1">Checked in {new Date(a.checked_in_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>}
                </div>
                <Button
                  onClick={()=>toggleCheckIn(a)}
                  disabled={busy}
                  className={`shrink-0 rounded-full h-12 px-5 ${isIn ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-cream text-black hover:bg-white"}`}
                  data-testid={`door-check-${a.ticket_id}`}
                >
                  {busy ? "…" : isIn ? <><CheckCircle2 size={18} className="mr-2" /> In</> : <><CircleDashed size={18} className="mr-2" /> Check in</>}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <QrScannerSheet
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={handleScannedCode}
      />

      <AddAttendeeDialog
        open={walkupOpen}
        onOpenChange={setWalkupOpen}
        eventId={eventId}
        eventTitle={event?.title}
        onCreated={load}
      />
    </div>
  );
}

/**
 * Full-screen QR scanner using html5-qrcode. Starts the back camera when opened,
 * stops cleanly when closed. On a successful scan the sheet stays open so the
 * host can scan the next guest without tapping anything.
 */
function QrScannerSheet({ open, onOpenChange, onScan }) {
  const containerId = "door-qr-reader";
  const scannerRef = useRef(null);
  const [status, setStatus] = useState("Point the camera at a ticket QR");
  const [manual, setManual] = useState("");
  const lastScanRef = useRef({ code: "", at: 0 });

  useEffect(() => {
    if (!open) return;
    let scanner;
    let cancelled = false;
    (async () => {
      try {
        scanner = new Html5Qrcode(containerId, /* verbose */ false);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: { exact: "environment" } },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            // Debounce repeat decodes of the same code within 2.5s so the host
            // doesn't spam the API while they move the camera to the next guest.
            const now = Date.now();
            if (decoded === lastScanRef.current.code && now - lastScanRef.current.at < 2500) return;
            lastScanRef.current = { code: decoded, at: now };
            setStatus(`Scanned ${decoded}`);
            onScan(decoded);
          },
          () => { /* ignore per-frame failures */ },
        );
        if (cancelled) scanner.stop().catch(()=>{});
      } catch (err) {
        // Retry without the strict "environment" constraint for desktops / front cams.
        try {
          await scannerRef.current?.start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 240, height: 240 } },
            (decoded) => {
              const now = Date.now();
              if (decoded === lastScanRef.current.code && now - lastScanRef.current.at < 2500) return;
              lastScanRef.current = { code: decoded, at: now };
              setStatus(`Scanned ${decoded}`);
              onScan(decoded);
            },
            () => {},
          );
        } catch (fallbackErr) {
          setStatus("Camera unavailable — type the ticket code below");
        }
      }
    })();
    return () => {
      cancelled = true;
      scannerRef.current?.stop().catch(()=>{}).finally(() => {
        scannerRef.current?.clear?.();
        scannerRef.current = null;
      });
    };
  }, [open, onScan]);

  if (!open) return null;

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    onScan(manual.trim());
    setManual("");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 text-cream flex flex-col" data-testid="door-scanner">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
        <div className="flex items-center gap-2">
          <Camera size={18} className="text-[hsl(46_65%_60%)]" />
          <div className="font-heading text-base">Scan ticket QR</div>
        </div>
        <button onClick={()=>onOpenChange(false)} className="text-neutral-400 hover:text-white" aria-label="Close scanner" data-testid="door-scanner-close"><X size={22} /></button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-start pt-6 px-4">
        <div id={containerId} className="w-full max-w-sm aspect-square rounded-2xl overflow-hidden bg-neutral-900 border border-neutral-800" />
        <div className="text-sm text-neutral-400 mt-4 text-center min-h-[1.25rem]" data-testid="door-scanner-status">{status}</div>
        <form onSubmit={submitManual} className="w-full max-w-sm mt-6 flex gap-2">
          <Input
            value={manual}
            onChange={e=>setManual(e.target.value.toUpperCase())}
            placeholder="Or type the code"
            className="h-11 rounded-full bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500 uppercase tracking-widest font-mono"
            data-testid="door-scanner-manual"
          />
          <Button type="submit" className="h-11 rounded-full bg-cream text-black hover:bg-white px-5" data-testid="door-scanner-manual-submit">Check in</Button>
        </form>
      </div>
    </div>
  );
}
